import React, { useState } from 'react';
import axios from 'axios';
import { Lock, Eye, EyeOff, Save, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Self-service password rotation for the currently authenticated user.
 * Uses the authenticated ``POST /api/auth/change-password`` endpoint —
 * NOT the email-based reset flow. Validates client-side before hitting
 * the server, but final policy enforcement remains server-side.
 */
const ChangePasswordCard = () => {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null); // {kind:'success'|'error', msg}

  const localValidate = () => {
    if (!current || !next || !confirm) return 'All fields are required';
    if (next !== confirm) return 'New password and confirmation do not match';
    if (next === current) return 'New password must be different from current';
    if (next.length < 8) return 'Password must be at least 8 characters';
    if (!/[A-Z]/.test(next)) return 'Password must include an uppercase letter';
    if (!/[a-z]/.test(next)) return 'Password must include a lowercase letter';
    if (!/\d/.test(next)) return 'Password must include a number';
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(next)) return 'Password must include a special character';
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    setStatus(null);
    const err = localValidate();
    if (err) { setStatus({ kind: 'error', msg: err }); return; }
    setSaving(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: current, new_password: next },
        { withCredentials: true }
      );
      setStatus({ kind: 'success', msg: 'Password updated — you will stay signed in on this device.' });
      setCurrent(''); setNext(''); setConfirm('');
    } catch (e2) {
      setStatus({ kind: 'error', msg: e2?.response?.data?.detail || 'Failed to change password' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      className="mt-8 bg-white border border-slate-200 rounded-md p-6"
      data-testid="change-password-card"
    >
      <div className="flex items-center gap-2 mb-5">
        <Lock className="w-5 h-5 text-[#002FA7]" />
        <h2
          className="text-xl font-semibold tracking-tight text-slate-900"
          style={{ fontFamily: 'Work Sans, sans-serif' }}
        >
          Change password
        </h2>
      </div>

      <form onSubmit={submit} className="space-y-4 max-w-md">
        <PasswordField
          testid="cp-current"
          label="Current password"
          value={current}
          onChange={setCurrent}
          show={showCurrent}
          setShow={setShowCurrent}
          autoComplete="current-password"
        />
        <PasswordField
          testid="cp-new"
          label="New password"
          value={next}
          onChange={setNext}
          show={showNext}
          setShow={setShowNext}
          autoComplete="new-password"
        />
        <PasswordField
          testid="cp-confirm"
          label="Confirm new password"
          value={confirm}
          onChange={setConfirm}
          show={showNext}
          setShow={setShowNext}
          autoComplete="new-password"
        />

        <div className="text-xs text-slate-500 leading-relaxed">
          Must be at least 8 characters and include an uppercase letter,
          a lowercase letter, a number, and a special symbol.
        </div>

        {status && (
          <div
            data-testid={`cp-status-${status.kind}`}
            className={`flex items-start gap-2 text-sm rounded-md p-3 ${
              status.kind === 'success'
                ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}
          >
            {status.kind === 'success'
              ? <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              : <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
            <span>{status.msg}</span>
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="submit"
            data-testid="cp-submit"
            disabled={saving}
            className="inline-flex items-center gap-2 bg-[#002FA7] hover:bg-[#00247D] disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-md px-4 py-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Updating…' : 'Update password'}
          </button>
        </div>
      </form>
    </section>
  );
};

const PasswordField = ({ testid, label, value, onChange, show, setShow, autoComplete }) => (
  <div>
    <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1">
      {label}
    </label>
    <div className="relative">
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        data-testid={testid}
        className="w-full px-3 py-2 pr-10 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
        required
      />
      <button
        type="button"
        onClick={() => setShow(!show)}
        className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600"
        aria-label={show ? 'Hide password' : 'Show password'}
      >
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  </div>
);

export default ChangePasswordCard;
